import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, StatusBadge, inr } from "@/components/bits";
import { KanbanBoard } from "@/components/KanbanBoard";
import api, { errMsg, fmtDate } from "@/lib/api";

export default function JobATS() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [apps, setApps] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [fbFor, setFbFor] = useState(null);
  const [fb, setFb] = useState({ communication: 4, technical: 4, culture: 4, overall: 4, recommendation: "next_round", notes: "" });

  const load = useCallback(async () => {
    const [j, a, iv] = await Promise.all([
      api.get(`/employer/jobs/${id}`),
      api.get(`/employer/jobs/${id}/applications`),
      api.get(`/employer/jobs/${id}/interviews`),
    ]);
    setJob(j.data);
    setApps(a.data.applications);
    setInterviews(iv.data.interviews);
  }, [id]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const submitFeedback = async () => {
    try {
      await api.post(`/interviews/${fbFor.id}/feedback`, {
        scores: { communication: Number(fb.communication), technical: Number(fb.technical), culture: Number(fb.culture) },
        overall: Number(fb.overall), recommendation: fb.recommendation, notes: fb.notes,
      });
      toast.success("Feedback recorded");
      setFbFor(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!job) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="job-ats-page">
      <Link to="/employer/jobs" className="text-sm text-[#2563EB] font-medium inline-flex items-center gap-1 mb-5">
        <ArrowLeft className="w-4 h-4" /> All jobs
      </Link>
      <div className="bg-white border border-border/60 rounded-xl p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-heading font-bold text-[#0B1B3D]">{job.title}</h1>
              <StatusBadge status={job.status} />
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">
              {job.location} · {job.experience_min}–{job.experience_max} yrs · {inr(job.salary_min)}–{inr(job.salary_max)} ·{" "}
              <span className="capitalize">{(job.hiring_preference || "own").replace(/_/g, " ")}</span>
              {job.recruitment_payment_model !== "none" && (
                <span className="capitalize"> · {job.recruitment_payment_model}</span>
              )}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">Posted {fmtDate(job.created_at)}</p>
        </div>
        {job.review_history?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border/60">
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Review history</p>
            <div className="space-y-1.5">
              {job.review_history.map((r, i) => (
                <p key={i} className="text-xs text-muted-foreground">
                  <span className="font-semibold capitalize text-[#0B1B3D]">{r.action}</span> — {r.reason || "—"} ({fmtDate(r.at)})
                </p>
              ))}
            </div>
          </div>
        )}
      </div>

      {apps.length === 0 ? (
        <EmptyState
          title="No applications yet"
          description={job.status === "published" ? "Share the job link to attract candidates." : "This job will receive applications once it is approved and published."}
          testId="apps-empty"
        />
      ) : (
        <KanbanBoard applications={apps} onRefresh={load} showJob={false} />
      )}

      {interviews.length > 0 && (
        <div className="bg-white border border-border/60 rounded-xl p-6 mt-8" data-testid="interviews-panel">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Interviews & scorecards</h3>
          <div className="space-y-3">
            {interviews.map((iv) => (
              <div key={iv.id} className="border border-border/60 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`interview-${iv.id}`}>
                <div>
                  <p className="font-semibold text-sm">{iv.candidate_name} — {iv.round}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                    {iv.mode} · {iv.scheduled_at ? new Date(iv.scheduled_at).toLocaleString("en-IN") : "Awaiting candidate slot"}
                  </p>
                  {iv.panel?.length > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-1">Panel: {iv.panel.join(", ")}</p>
                  )}
                  {iv.meeting_link && (
                    <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-[#2563EB] font-semibold mt-1 inline-block" data-testid={`meeting-${iv.id}`}>
                      Join video room →
                    </a>
                  )}
                  {iv.feedback && (
                    <p className="text-xs mt-1.5 flex items-center gap-1.5 text-[#0B1B3D]">
                      <Star className="w-3.5 h-3.5 text-[#F5A623] fill-[#F5A623]" />
                      {iv.feedback.overall}/5 · <span className="capitalize">{iv.feedback.recommendation.replace(/_/g, " ")}</span>
                      {iv.feedback.notes && <span className="text-muted-foreground"> — {iv.feedback.notes}</span>}
                    </p>
                  )}
                </div>
                {!iv.feedback ? (
                  <Button size="sm" className="rounded-full bg-[#0B1B3D]" onClick={() => { setFbFor(iv); setFb({ communication: 4, technical: 4, culture: 4, overall: 4, recommendation: "next_round", notes: "" }); }} data-testid={`feedback-${iv.id}`}>
                    Add Scorecard
                  </Button>
                ) : (
                  <StatusBadge status="completed" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!fbFor} onOpenChange={() => setFbFor(null)}>
        <DialogContent data-testid="feedback-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Scorecard — {fbFor?.candidate_name} ({fbFor?.round})</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {["communication", "technical", "culture", "overall"].map((k) => (
                <div key={k}>
                  <Label className="capitalize">{k} (1–5)</Label>
                  <Input type="number" min={1} max={5} value={fb[k]} onChange={(e) => setFb({ ...fb, [k]: e.target.value })} className="mt-1.5" data-testid={`fb-${k}`} />
                </div>
              ))}
            </div>
            <div>
              <Label>Recommendation</Label>
              <Select value={fb.recommendation} onValueChange={(v) => setFb({ ...fb, recommendation: v })}>
                <SelectTrigger className="mt-1.5" data-testid="fb-recommendation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="select">Select</SelectItem>
                  <SelectItem value="next_round">Next Round</SelectItem>
                  <SelectItem value="hold">Hold</SelectItem>
                  <SelectItem value="reject">Reject</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea rows={3} value={fb.notes} onChange={(e) => setFb({ ...fb, notes: e.target.value })} className="mt-1.5" data-testid="fb-notes" /></div>
            <Button onClick={submitFeedback} className="w-full rounded-full bg-[#0B1B3D]" data-testid="fb-submit-btn">Submit Feedback</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
